import { useId, useState, type KeyboardEvent } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

interface FormTagsInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  placeholder?: string
  // Optional type-ahead list; free text is still accepted when set.
  suggestions?: readonly string[]
}

const MAX_SUGGESTIONS = 8

/**
 * Freeform chip input for string[] fields (job skills/education, candidate
 * search filters) — unlike FormMultiSelect, values aren't drawn from a fixed
 * option list, so choices.js isn't a fit here. With `suggestions`, typing
 * shows matches that start with the text first, then ones containing it.
 */
export function FormTagsInput<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Type and press Enter…',
  suggestions,
}: FormTagsInputProps<TFieldValues>) {
  const { field } = useController({ control, name })
  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const listId = useId()
  const values: string[] = field.value ?? []

  const query = draft.trim().toLowerCase()
  const isTaken = (s: string) => values.some((v) => v.toLowerCase() === s.toLowerCase())
  const matches =
    query === '' || !suggestions
      ? []
      : [
          ...suggestions.filter((s) => s.toLowerCase().startsWith(query)),
          ...suggestions.filter(
            (s) => !s.toLowerCase().startsWith(query) && s.toLowerCase().includes(query),
          ),
        ]
          .filter((s) => !isTaken(s))
          .slice(0, MAX_SUGGESTIONS)
  const showList = open && matches.length > 0

  const addTag = (raw: string) => {
    const typed = raw.trim()
    // Snap to a suggestion's spelling when it matches ignoring case ("html" → "HTML").
    const tag = suggestions?.find((s) => s.toLowerCase() === typed.toLowerCase()) ?? typed
    if (tag && !isTaken(tag)) {
      field.onChange([...values, tag])
    }
    setDraft('')
    setActiveIndex(-1)
  }

  const removeTag = (tag: string) => {
    field.onChange(values.filter((v) => v !== tag))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && matches.length > 0) {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => (i + 1) % matches.length)
    } else if (e.key === 'ArrowUp' && matches.length > 0) {
      e.preventDefault()
      setActiveIndex((i) => (i <= 0 ? matches.length - 1 : i - 1))
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    } else if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const picked = showList && activeIndex >= 0 ? matches[activeIndex] : undefined
      addTag(picked ?? draft)
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      const lastTag = values[values.length - 1]
      if (lastTag) removeTag(lastTag)
    }
  }

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
        </label>
      )}
      {values.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {values.map((tag) => (
            <span
              key={tag}
              className="badge bg-light text-dark border d-flex align-items-center gap-2 p-2"
            >
              {tag}
              <button
                type="button"
                className="btn-close fs-10"
                aria-label={`Remove ${tag}`}
                onClick={() => removeTag(tag)}
              />
            </span>
          ))}
        </div>
      )}
      <div className="position-relative">
        <input
          id={name}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={draft}
          autoComplete="off"
          role={suggestions ? 'combobox' : undefined}
          aria-expanded={suggestions ? showList : undefined}
          aria-controls={suggestions ? listId : undefined}
          aria-autocomplete={suggestions ? 'list' : undefined}
          aria-activedescendant={
            showList && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
          }
          onChange={(e) => {
            setDraft(e.target.value)
            setOpen(true)
            setActiveIndex(-1)
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setOpen(false)
            addTag(draft)
          }}
        />
        {showList && (
          <ul
            id={listId}
            role="listbox"
            className="list-group position-absolute w-100 shadow-sm mt-1"
            style={{ zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}
          >
            {matches.map((s, i) => (
              <li
                key={s}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`list-group-item list-group-item-action py-2 ${i === activeIndex ? 'active' : ''}`.trim()}
                style={{ cursor: 'pointer' }}
                // mousedown + preventDefault keeps focus in the input, so its
                // onBlur doesn't first add the half-typed draft as a tag.
                onMouseDown={(e) => {
                  e.preventDefault()
                  addTag(s)
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
